/** @file Fetches and decodes identity page images (premultiplied), caching results and failures by sha256. */
import { decodePng } from '@pose2d/image-io/decodePng';
import { premultiply } from '@pose2d/image-io/premultiply';
import type { RgbaImage } from '@pose2d/math/types';

/** One image, or null for a 404, a network failure or a PNG decodePng refuses. */
async function decoded(fetchPng: (sha256: string) => Promise<Uint8Array | null>, id: string): Promise<RgbaImage | null> {
  try {
    const bytes = await fetchPng(id);
    return bytes === null ? null : premultiply(decodePng(bytes));
  } catch {
    return null;
  }
}

/** Loads every id not yet in `cache`; true when anything new landed (the caller rebuilds its page set). */
export async function loadPages(ids: readonly string[], fetchPng: (sha256: string) => Promise<Uint8Array | null>, cache: Map<string, RgbaImage | null>): Promise<boolean> {
  const wanted = [...new Set(ids)].filter((id) => !cache.has(id));
  const results = await Promise.all(wanted.map((id) => decoded(fetchPng, id)));
  wanted.forEach((id, i) => { cache.set(id, results[i] ?? null); });
  return wanted.length > 0;
}
